// Komponent wyświetlający linki nawigacyjne z CMS w formie mobilnej
// @ts-nocheck

import React, { useState, useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import classnames from 'classnames';
import { useGetCmsSectionArticle } from 'api/endpoints/useGetCmsSectionArticle';
import { useSelector } from 'store';
import subcategoriesBanner from 'assets/images/subcategories-banner.jpg';

import styles from 'theme/components/containers/MobileNavigationOverlay/components/MobileNavigationLinks/MobileNavigationLinks.module.scss';

interface IProps {
  onLinkClick?: () => void;
  onSubcategoriesViewChange?: (isOpen: boolean) => void;
}

interface NavigationItem {
  id: string;
  name: string;
  url?: string;
  hasDropdown: boolean;
  dropdownItems?: Array<{
    id: string;
    name: string;
    url: string;
    icon?: string;
  }>;
}

const MobileNavigationLinks: React.FC<IProps> = ({ onLinkClick, onSubcategoriesViewChange }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { token } = useSelector((state) => state.auth);
  const [selectedItem, setSelectedItem] = useState<NavigationItem | null>(null);
  const [navigationItems, setNavigationItems] = useState<NavigationItem[]>([]);

  // Pobieranie HTML z CMS
  const { data: navigationBarData, refetch: refetchNavigationBar } =
    useGetCmsSectionArticle("HEADER_NAVIGATION", "HEADER_NAVIGATION_BAR", {
      enabled: false,
    });

  useEffect(() => {
    refetchNavigationBar();
  }, [refetchNavigationBar, token]);

  // Funkcja do wyciągnięcia HTML z artykułu CMS
  const getNavigationBarHtml = useCallback(() => {
    if (!navigationBarData?.article_fields) return null;
    
    const htmlField = navigationBarData.article_fields.find(
      (field) => field.type === "HTMLBLOCK"
    );
    
    if (htmlField?.value) {
      if (Array.isArray(htmlField.value)) {
        const currentLang = navigationBarData.language || 'pl';
        const translation = htmlField.value.find((t: any) => t.language === currentLang);
        return translation?.translation || translation?.value || htmlField.value[0]?.translation || htmlField.value[0]?.value || null;
      }
      if (typeof htmlField.value === 'string') {
        return htmlField.value;
      }
    }
    
    return null;
  }, [navigationBarData]);

  // Parsowanie HTML i ekstrakcja linków nawigacyjnych
  useEffect(() => {
    const html = getNavigationBarHtml();
    if (!html) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const items: NavigationItem[] = [];

    // Znajdź wszystkie linki w .navigationLinksLeft i .navigationLinksRight
    const allLinks = doc.querySelectorAll('.navigationLinksLeft .link, .navigationLinksRight .link');
    allLinks.forEach((link, index) => {
      const linkElement = link.cloneNode(true) as HTMLElement;
      // Usuń wszystkie elementy SVG przed pobraniem tekstu
      linkElement.querySelectorAll('svg').forEach(svg => svg.remove());
      const name = linkElement.textContent?.trim() || '';
      const url = (link as HTMLElement).getAttribute('data-nav-link') || (link as HTMLElement).getAttribute('href') || '';
      const hasDropdown = (link as HTMLElement).hasAttribute('data-dropdown-toggle');
      
      let dropdownItems: NavigationItem['dropdownItems'] = [];
      
      if (hasDropdown) {
        const dropdownId = (link as HTMLElement).getAttribute('data-dropdown-toggle');
        if (dropdownId) {
          const dropdownMenu = doc.getElementById(dropdownId);
          if (dropdownMenu) {
            const dropdownLinks = dropdownMenu.querySelectorAll('.dropdownItem');
            dropdownLinks.forEach((item) => {
              const itemElement = item.cloneNode(true) as HTMLElement;
              // Zapisz ikonę przed usunięciem SVG
              const iconElement = (item as HTMLElement).querySelector('svg');
              const icon = iconElement ? iconElement.outerHTML : undefined;
              // Usuń wszystkie elementy SVG przed pobraniem tekstu
              itemElement.querySelectorAll('svg').forEach(svg => svg.remove());
              const itemName = itemElement.textContent?.trim() || '';
              const itemUrl = (item as HTMLElement).getAttribute('href') || (item as HTMLElement).getAttribute('data-nav-link') || '';
              
              dropdownItems.push({
                id: `${dropdownId}-${dropdownItems.length}`,
                name: itemName,
                url: itemUrl,
                icon
              });
            });
          }
        }
      }

      items.push({
        id: `nav-${index}`,
        name,
        url: url !== '#' ? url : undefined,
        hasDropdown,
        dropdownItems: dropdownItems.length > 0 ? dropdownItems : undefined
      });
    });

    setNavigationItems(items);
  }, [navigationBarData, getNavigationBarHtml]);

  const isExternalUrl = (url: string) =>
    url.startsWith('http://') || url.startsWith('https://');

  // Funkcja otwierająca widok podkategorii
  const handleItemClick = (item: NavigationItem) => {
    if (item.hasDropdown && item.dropdownItems && item.dropdownItems.length > 0) {
      setSelectedItem(item);
      onSubcategoriesViewChange?.(true);
    } else if (item.url && !isExternalUrl(item.url)) {
      navigate(`/${i18n.language}${item.url}`);
      onLinkClick?.();
    }
  };

  // Funkcja cofająca do widoku głównego
  const handleBackClick = () => {
    setSelectedItem(null);
    onSubcategoriesViewChange?.(false);
  };

  // Renderowanie widoku podkategorii
  if (selectedItem) {
    return (
      <div className={styles.mobileSubcategoriesView}>
        <div className={styles.mobileSubcategoriesHeader}>
          <button className={styles.backButton} onClick={handleBackClick}>
            <ChevronLeft />
          </button>
          <h2 className={styles.categoryTitle}>{selectedItem.name}</h2>
        </div>
        <div className={styles.mobileSubcategoriesList}>
          {selectedItem.dropdownItems?.map((subItem) => (
            <a
              key={subItem.id}
              href={subItem.url}
              className={styles.mobileSubcategoryItem}
              onClick={onLinkClick}
            >
              {subItem.icon && (
                <span
                  className={styles.itemIcon}
                  dangerouslySetInnerHTML={{ __html: subItem.icon }}
                />
              )}
              <span className={styles.itemName}>{subItem.name}</span>
            </a>
          ))}
        </div>
        <div className={styles.mobileSubcategoriesBanner}>
          <img
            src={subcategoriesBanner}
            alt="Banner"
            className={styles.bannerImage}
          />
        </div>
      </div>
    );
  }

  // Renderowanie głównej listy linków
  return (
    <div className={styles.navigationLinksContainer}>
      {navigationItems.map((item) => (
        item.hasDropdown ? (
          <button
            key={item.id}
            onClick={() => handleItemClick(item)}
            className={classnames(styles.navigationLinkItem, styles.hasDropdown)}
          >
            <span className={styles.linkName}>{item.name}</span>
            <ChevronRight className={styles.chevronIcon} />
          </button>
        ) : (
          <a
            key={item.id}
            href={item.url}
            className={styles.navigationLinkItem}
            onClick={onLinkClick}
          >
            <span className={styles.linkName}>{item.name}</span>
          </a>
        )
      ))}
    </div>
  );
};

export default MobileNavigationLinks;

